import { toUtcAdjusted } from '@noor/lib';
import type {
  OrderCancelledRoleTab,
  OrderEquipmentTab,
  OrderStageTab,
} from '../model/stages';
import type {
  OrderIdSort,
  OrderListFilter,
  OrderListType,
  OrderTab,
} from '../model/list-params';

/* eslint-disable @typescript-eslint/prefer-nullish-coalescing --
   Ported verbatim from cpanel `features/orders/model/filters.ts` and
   `hooks/useCancelledOrdersPage.ts`. The `||` falsy-collapse on the toggle
   filters is INTENTIONAL: a falsy value drops to `undefined` so the serializer
   omits it. `??` would keep `false` / `''`. */

interface QueryValues {
  search?: string | undefined;
  limit?: string | number | undefined;
  offset?: string | number | undefined;
  start_date?: string | undefined;
  end_date?: string | undefined;
}

/** cpanel's entity filter: the vendor/client select or a URL-seeded client. */
export interface OrderClientRef {
  id?: string | number | undefined;
  type?: 'client' | 'vendor' | undefined;
}

export interface OrderCourierRef {
  id?: string | number | undefined;
}

interface BuildOrdersListFilterArgs {
  type: OrderListType;
  values: QueryValues;
  status: OrderTab;
  stageTab: OrderStageTab;
  equipmentTab: OrderEquipmentTab;
  cancelledByRoleTab: OrderCancelledRoleTab;
  client: OrderClientRef | null;
  courier: OrderCourierRef | null;
  /** ID-column sort; `undefined` = cpanel sort position 0 (no sort key). */
  idSort: OrderIdSort | undefined;
  filter: Record<string, unknown>;
  isStatusPriorityCustomized: boolean;
  statusPriorityOrder: number[];
  /**
   * Page size to send when no explicit `limit` is set — cpanel sent its
   * `tableLimit` default on first open instead of omitting `limit`.
   */
  defaultLimit?: string | number | undefined;
}

/** cpanel sort positions: 1 → `true`, 2 → `false`, 0 → omitted. */
function lateSortFor(idSort: OrderIdSort | undefined) {
  return idSort ? { sort_by_late_orders: idSort === 'asc' } : {};
}

function spreadToggleFilter(filter: Record<string, unknown>, keys: readonly string[]) {
  return keys.reduce<Record<string, unknown>>(
    (sum, key) => ({ ...sum, [key]: filter[key] || undefined }),
    {},
  );
}

/**
 * Build the snake_case request filter for `GET /admin/orders`. Ported verbatim
 * from cpanel `buildOrdersListFilter`: the `display_id` `/`→`\` escape (or the
 * `xolodilnik` marker on the market list), the `toUtcAdjusted` (+5h) date
 * window with `till_date` +1 day, the CURRENT-priority vs
 * `sort_by_late_orders` branch, the per-tab `is_*` flags with their sub-tab
 * scopes (`current_status_tab` / `equipment_scope` / `cancelled_by_roles`),
 * the orders-only `is_paid`/`is_courier_debt` block, and falsy-collapse filter
 * spreading. Pinned by `build-list-filter.test.ts`.
 */
export function buildOrdersListFilter({
  type,
  values,
  status,
  stageTab,
  equipmentTab,
  cancelledByRoleTab,
  client,
  courier,
  idSort,
  filter,
  isStatusPriorityCustomized,
  statusPriorityOrder,
  defaultLimit,
}: BuildOrdersListFilterArgs): OrderListFilter {
  return {
    display_id: type === 'orders' ? values?.search?.replaceAll('/', '\\') : 'xolodilnik',
    limit: values?.limit ?? defaultLimit,
    skip: values?.offset,
    vendor_id: client?.id,
    courier_id: courier?.id,
    from_date: toUtcAdjusted(values?.start_date).toISOString(),
    till_date: toUtcAdjusted(values?.end_date).add(1, 'day').toISOString(),
    ...(status === 'CURRENT' &&
    isStatusPriorityCustomized &&
    statusPriorityOrder.length > 0
      ? { status_id_order: statusPriorityOrder.join(',') }
      : lateSortFor(idSort)),
    // The active list must always carry both is_active=true and
    // current_status_tab; the backend ignores the tab without is_active.
    current_status_tab: status === 'CURRENT' ? stageTab : undefined,
    is_active: status === 'CURRENT' ? true : undefined,
    is_scheduled: status === 'SHCHEDULED' ? true : undefined,
    is_finished: status === 'COMPLETED' ? true : undefined,
    is_cancelled: status === 'CANCELLED' ? true : undefined,
    cancelled_by_roles:
      status === 'CANCELLED' && cancelledByRoleTab !== 'all'
        ? cancelledByRoleTab
        : undefined,
    is_not_found: status === 'NOT-FOUND' ? true : undefined,
    is_returned: status === 'RETURNED' ? true : undefined,
    is_in_search: status === 'SEARCH' ? true : undefined,
    // The backend ignores equipment_scope without is_in_search; `all` sends none.
    equipment_scope:
      status === 'SEARCH' && equipmentTab !== 'all' ? equipmentTab : undefined,
    is_cancelled_with_balance: status === 'NEG_BALANCE' ? true : undefined,
    ...(type === 'orders'
      ? {
          is_paid: status === 'is_paid' ? false : undefined,
          is_courier_debt: status === 'is_courier_debt' ? false : undefined,
        }
      : {}),
    ...spreadToggleFilter(filter, Object.keys(filter)),
  };
}

interface BuildCancelledOrdersFilterArgs {
  values: QueryValues;
  client: OrderClientRef | null;
  courier: OrderCourierRef | null;
  idSort: OrderIdSort | undefined;
  cancelledByRoleTab: OrderCancelledRoleTab;
  filter: Record<string, unknown>;
}

/**
 * Filter for the standalone cancelled-orders page — ported verbatim from
 * cpanel `useCancelledOrdersPage`: always `is_cancelled`, no market marker, no
 * tab flags beyond the who-cancelled scope, and no default `limit`.
 */
export function buildCancelledOrdersListFilter({
  values,
  client,
  courier,
  idSort,
  cancelledByRoleTab,
  filter,
}: BuildCancelledOrdersFilterArgs): OrderListFilter {
  return {
    display_id: values?.search?.replaceAll('/', '\\'),
    limit: values?.limit,
    skip: values?.offset,
    vendor_id: client?.id,
    courier_id: courier?.id,
    from_date: toUtcAdjusted(values?.start_date).toISOString(),
    till_date: toUtcAdjusted(values?.end_date).add(1, 'day').toISOString(),
    ...lateSortFor(idSort),
    is_cancelled: true,
    cancelled_by_roles: cancelledByRoleTab !== 'all' ? cancelledByRoleTab : undefined,
    ...spreadToggleFilter(filter, Object.keys(filter)),
  };
}
